import clsx from "clsx";
import type { ComponentType, ReactNode } from "react";

import type { CardBackground, CardRadius } from "@/puck/types";
import { getCardSurfaceClasses } from "@/utils/cardStyles";

export type MarketingFeatureCardProps = {
  title: string;
  description: string;
  visual?: ReactNode;
  background?: CardBackground;
  radius?: CardRadius;
  bordered?: boolean;
  /** When true, applies lift + shadow on hover (respects reduced motion). */
  interactive?: boolean;
  className?: string;
};

export function MarketingFeatureCard({
  title,
  description,
  visual,
  background = "voiceCard",
  radius = "2xl",
  bordered = true,
  interactive = true,
  className,
}: Readonly<MarketingFeatureCardProps>) {
  const hasText = Boolean(title || description);

  return (
    <article
      className={clsx(
        "flex h-full flex-col overflow-hidden",
        getCardSurfaceClasses({ background, radius, bordered }),
        hasText ? "px-5 pt-5" : "p-6",
        visual && hasText && "pb-5",
        !hasText && visual && "pb-6",
        interactive &&
          "transition-transform transition-shadow duration-200 ease-out motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-xl",
        className,
      )}
    >
      {title ? (
        <h3 className="font-monument text-lg font-medium text-black">{title}</h3>
      ) : null}
      {description ? (
        <p className="mt-2 min-h-[3.75rem] font-monument text-sm leading-relaxed text-black/70">
          {description}
        </p>
      ) : null}
      {visual ? (
        <div
          className={clsx(
            "relative min-h-[16rem] flex-1 overflow-hidden",
            hasText ? "mt-6" : "mt-0",
          )}
        >
          {visual}
        </div>
      ) : null}
    </article>
  );
}

export type MarketingFeatureCardSlotProps = {
  title: string;
  description: string;
  visual?: ComponentType<{ className?: string }>;
  background?: CardBackground;
  radius?: CardRadius;
  bordered?: boolean;
  interactive?: boolean;
  className?: string;
};

export function MarketingFeatureCardSlot({
  title,
  description,
  visual: Visual,
  background = "voiceCard",
  radius = "2xl",
  bordered = true,
  interactive = true,
  className,
}: Readonly<MarketingFeatureCardSlotProps>) {
  return (
    <MarketingFeatureCard
      title={title}
      description={description}
      background={background}
      radius={radius}
      bordered={bordered}
      interactive={interactive}
      className={className}
      visual={Visual ? <Visual className="h-full" /> : null}
    />
  );
}
